import {
  getLiveProjection,
  isSavableLiveChart,
  trendLabel,
  trendValues,
} from "../lib/liveChartContract";
import { projectWidgetData } from "../lib/projectWidgetData";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ChartSpec } from "@/types";
import type {
  DashboardFilters,
  EmissionsSankeyItem,
  EmissionsByScopeAndMonthResponse,
  EmissionsByScopeAndCategoryResponse,
  YearEmission,
} from "../types/dashboard";
import * as api from "../api/dashboardService";
import {
  buildEmissionQueryParams,
  resolveWidgetData,
  type EmissionQueryParams,
} from "../lib/resolveWidgetData";
import { useProtocol } from "@/lib/protocol";
import {
  useHasBusinessUnits,
  useSelectedOrAllBusinessUnitIds,
  useSupplierAllocations,
} from "./useDashboardData";
import {
  mergeAllocationsIntoSankey,
  mergeAllocationsIntoScopeMonth,
  mergeAllocationsIntoScopeCategory,
  mergeAllocationsIntoYear,
} from "../lib/supplierAllocations";
import { STALE } from "@/lib/query-constants";
import { useHasPlanFeature } from "@/hooks/usePermissions";
import { useThemeStore } from "@/stores/theme";
import { emissionSankeyData } from "../lib/emissionSankey";
import { sankeyHeight } from "@/components/charts/sankeyLayout";
import { restoreLegacyRankedWidget } from "../lib/legacyWidget";

type SourceFetch = (params: EmissionQueryParams) => Promise<unknown>;

const LIVE_SOURCES: Record<string, SourceFetch> = {
  allemissions: (p) => api.getAllEmissions(p),
  allemissionsbymonth: (p) => api.getEmissionsByMonth(p),
  emissionsbyscopeandcategories: (p) => api.getEmissionsByScopeAndCategories(p),
  emissionsbyunit: (p) => api.getEmissionsByUnit(p),
  emissionsbyactivity: (p) => api.getEmissionsByActivity(p),
  emissionsbyyear: (p) => api.getFilteredEmissionsByYear(p),
};

/**
 * The canonical, supported set of live data sources. A natural-language chart
 * may only be added to the dashboard (and rendered with dynamic data) when its
 * `dataSource.source` is one of these and its projection is supported.
 */
export const SUPPORTED_LIVE_SOURCES = new Set<string>(
  Object.keys(LIVE_SOURCES),
);

export function isLiveCapableSource(source: string | undefined): boolean {
  return !!source && SUPPORTED_LIVE_SOURCES.has(source);
}

export function isLiveCapableChart(spec: ChartSpec): boolean {
  return isSavableLiveChart(spec);
}

interface LiveSpecResult {
  spec: ChartSpec;
  isLive: boolean;
  refreshing: boolean;
  failed: boolean;
  status: "unsupported" | "static" | "pending" | "empty" | "error" | "ready";
  retry: () => unknown;
}

type EmissionUnit = "kgCO2e" | "tCO2e";

const SOURCE_UNIT: Record<string, EmissionUnit> = {
  allemissions: "kgCO2e",
  allemissionsbymonth: "kgCO2e",
  emissionsbyscopeandcategories: "kgCO2e",
  emissionsbyunit: "kgCO2e",
  emissionsbyactivity: "kgCO2e",
  emissionsbyyear: "tCO2e",
};

function convertValues(
  values: number[],
  fromUnit: EmissionUnit,
  toUnit: EmissionUnit,
): number[] {
  if (fromUnit === toUnit) return values;
  const factor = fromUnit === "tCO2e" ? 1000 : 1 / 1000;
  return values.map((v) => v * factor);
}

export function useWidgetLiveSpec(
  savedSpec: ChartSpec,
  filters: DashboardFilters,
): LiveSpecResult {
  const spec = useMemo(() => restoreLegacyRankedWidget(savedSpec), [savedSpec]);
  const protocol = useProtocol();
  const isDark = useThemeStore((s) => s.theme === "dark");
  const projection = getLiveProjection(spec);
  const isLive = !!projection;
  const unsupported = !!spec.dataSource && !isLive;
  const source = spec.dataSource?.source ?? "";
  const hasBusinessUnits = useHasBusinessUnits();
  const businessUnitIds = useSelectedOrAllBusinessUnitIds(filters);
  const hasEmissions = useHasPlanFeature("EMISSIONS");
  const canRead = hasEmissions && hasBusinessUnits && !!businessUnitIds;
  const allocations = useSupplierAllocations(
    filters,
    canRead &&
      isLive &&
      [
        "allemissions",
        "allemissionsbymonth",
        "emissionsbyscopeandcategories",
        "emissionsbyyear",
      ].includes(source),
  );

  const query = useQuery({
    queryKey: [
      "dashboard",
      "widget-live",
      source,
      protocol,
      filters,
      businessUnitIds,
    ],
    queryFn: () => {
      const fetch = LIVE_SOURCES[source];
      if (!fetch) {
        return Promise.reject(new Error(`Unknown widget source: ${source}`));
      }
      return fetch(buildEmissionQueryParams(filters, businessUnitIds));
    },
    enabled: isLive && canRead,
    staleTime: STALE.ONE_MIN,
  });

  const data = useMemo(() => {
    // Validate before merging: a malformed base response must remain an error.
    if (
      !isLive ||
      query.data === undefined ||
      !resolveWidgetData(source, query.data)
    )
      return query.data;
    if (source === "allemissions")
      return mergeAllocationsIntoSankey(
        query.data as EmissionsSankeyItem[],
        allocations.breakdown,
        protocol,
      );
    if (source === "allemissionsbymonth")
      return mergeAllocationsIntoScopeMonth(
        query.data as EmissionsByScopeAndMonthResponse,
        allocations.breakdown,
      );
    if (source === "emissionsbyyear")
      return mergeAllocationsIntoYear(
        query.data as YearEmission[],
        allocations.breakdown,
      );
    if (source === "emissionsbyscopeandcategories")
      return mergeAllocationsIntoScopeCategory(
        query.data as EmissionsByScopeAndCategoryResponse,
        allocations.breakdown,
        protocol,
      );
    return query.data;
  }, [isLive, source, query.data, allocations.breakdown, protocol]);
  const resolved = useMemo(
    () =>
      projection && data !== undefined
        ? projectWidgetData(source, data, projection)
        : null,
    [projection, source, data],
  );
  const isError = query.isError || allocations.isError;
  const isLoading = !query.isSuccess || allocations.isLoading;

  const sourceUnit = SOURCE_UNIT[source] ?? "kgCO2e";
  const targetUnit: EmissionUnit = filters.uom === "tCO2e" ? "tCO2e" : "kgCO2e";

  const liveSpec = useMemo((): ChartSpec => {
    if (!isLive && !unsupported) return spec;

    const emptySpec: ChartSpec = {
      ...spec,
      data: {
        ...spec.data,
        labels: [],
        datasets: [],
        links: [],
        unit: targetUnit,
        respectUnit: true,
      },
    };
    if (!projection || !canRead || !resolved || isError || isLoading)
      return emptySpec;

    if (spec.chartType === "sankey" && source === "allemissions") {
      const freshData = emissionSankeyData(
        data as EmissionsSankeyItem[],
        targetUnit,
        isDark,
      );
      return {
        ...spec,
        height: sankeyHeight(freshData.links ?? [], spec.height),
        data: freshData,
      };
    }

    if (resolved.series.length === 0) return emptySpec;

    const labels = resolved.labels;
    const datasets = resolved.series.map((series) => ({
      ...spec.data.datasets.find((ds) => ds.label === series.label),
      label: series.label,
      values: convertValues(series.values, sourceUnit, targetUnit),
    }));
    if (projection.trend && datasets.length) {
      datasets.push({
        label: trendLabel(projection.trend),
        type: projection.trend.type,
        values: trendValues(
          datasets[0].values,
          projection.trend.method,
          projection.trend.period,
        ),
      });
    }
    if (spec.chartType === "treemap") {
      // The renderer expects named points for treemaps, unlike scalar chart series.
      for (const dataset of datasets)
        dataset.values = dataset.values.map((value, i) => ({
          x: labels[i],
          value,
        })) as unknown as number[];
    }
    return {
      ...spec,
      data: {
        ...spec.data,
        labels,
        datasets,
        unit: targetUnit,
        respectUnit: true,
      },
    };
  }, [
    isLive,
    spec,
    source,
    resolved,
    data,
    projection,
    unsupported,
    sourceUnit,
    targetUnit,
    canRead,
    isError,
    isLoading,
    isDark,
  ]);

  const status: LiveSpecResult["status"] = unsupported
    ? "unsupported"
    : !isLive
      ? "static"
      : !canRead
        ? "empty"
        : isError || (query.isSuccess && !resolved)
          ? "error"
          : isLoading
            ? "pending"
            : !resolved?.labels.length
              ? "empty"
              : "ready";
  return {
    status,
    retry: () =>
      isLive && canRead
        ? Promise.all([query.refetch(), allocations.refetch()])
        : Promise.resolve([]),
    spec: liveSpec,
    isLive,
    refreshing:
      (query.isFetching || allocations.isFetching) && isLive && canRead,
    failed: status === "error",
  };
}
